import { useLatestRef } from '@heidi/utils';
import { useCallback, useEffect, useState } from 'react';
import { WORK_CHAT_HOME_PATH } from '../../../contracts/projects/chat-home-path';

// Work chat home, imported rather than restated — one declaration site — and
// re-exported under the name this module has always published.
export const WORK_CHAT_BASE_PATH = WORK_CHAT_HOME_PATH;

let chatInstanceCounter = 0;

const mintChatInstanceId = (): string => `work-chat-${++chatInstanceCounter}`;

const withoutLocalePrefix = (path: string, locales: readonly string[]): string => {
  if (!path.startsWith('/') || locales.length === 0) return path;
  const nextSlash = path.indexOf('/', 1);
  const head = nextSlash === -1 ? path.slice(1) : path.slice(1, nextSlash);
  const isLocale = locales.some((locale) => locale.toLowerCase() === head.toLowerCase());
  if (!isLocale) return path;
  return nextSlash === -1 ? '' : path.slice(nextSlash);
};

/**
 * `undefined` is the landing; `null` is not a Work chat at all. Router events
 * deliver locale-prefixed URLs (`/en/scribe/…`) while `router.asPath` does not,
 * so pass `router.locales`.
 */
export const workChatProjectIdFromUrl = (
  url: string,
  locales: readonly string[] = []
): string | undefined | null => {
  const path = withoutLocalePrefix(
    url.split(/[?#]/)[0]?.replace(/\/+$/, '') ?? '',
    locales
  );
  if (path === WORK_CHAT_BASE_PATH) return undefined;
  if (!path.startsWith(`${WORK_CHAT_BASE_PATH}/`)) return null;
  const [id, ...rest] = path.slice(WORK_CHAT_BASE_PATH.length + 1).split('/');
  return rest.length > 0 || !id ? null : decodeURIComponent(id);
};

/**
 * Delivers the Work chat the app's route has just SETTLED on, and returns its
 * own unsubscribe.
 *
 * This is the half of the router this hook actually needed, and naming it is
 * what let the hook cross. The route read itself is the app's — the page owns
 * `useRouter()` and its `routeChangeComplete` events — so what crosses is one
 * callback, already resolved through `workChatProjectIdFromUrl` above, with the
 * locale handling on the side that knows `router.locales`.
 *
 * The value is the same three-way answer that function gives: `undefined` is
 * the Work landing, a string is that chat, and `null` is a route that is not
 * Work at all and which this hook deliberately ignores.
 */
export type WorkChatRouteSettleSubscription = (
  listener: (projectId: string | undefined | null) => void
) => () => void;

export interface WorkChatSelection {
  projectId: string | undefined;
  /** The one key the chat subtree may carry: `projectId` or `agentRunId` would
   *  tear the chat down on its own first send. */
  chatInstanceId: string;
  /** Promote the mounted provisional chat without rotating. `false` means the
   *  lifetime moved on and the caller must abandon its continuation. */
  bindProvisionalChat: (projectId: string, forChatInstanceId: string) => boolean;
  /** Replace a routed chat with a fresh provisional lifetime. The returned id
   *  is the only lifetime the next bind may promote. */
  beginProvisionalChat: () => string;
  /** For a teardown that never navigates. A create still in flight for the old
   *  lifetime is refused at bind. */
  rotateChatLifetime: () => void;
}

/**
 * The route module never unmounts, so the selection is tracked rather than
 * inferred from a mount, and the first send binds in place rather than rotating.
 */
export const useWorkChatSelection = (
  routeProjectId: string | undefined,
  subscribeToRouteSettled: WorkChatRouteSettleSubscription
): WorkChatSelection => {
  const [selection, setSelection] = useState(() => ({
    projectId: routeProjectId,
    chatInstanceId: mintChatInstanceId(),
  }));
  const [lastRouteProjectId, setLastRouteProjectId] = useState(routeProjectId);

  // Render-phase, not an effect: a switch must not commit even one frame of the
  // old chat under the new URL. Minted in the updater so a thrown-away render
  // does not burn an id.
  if (routeProjectId !== lastRouteProjectId) {
    setLastRouteProjectId(routeProjectId);
    if (routeProjectId !== selection.projectId) {
      setSelection((previous) =>
        previous.projectId === routeProjectId
          ? previous
          : { projectId: routeProjectId, chatInstanceId: mintChatInstanceId() }
      );
    }
  }

  const selectionRef = useLatestRef(selection);
  const bindProvisionalChat = useCallback(
    (projectId: string, forChatInstanceId: string) => {
      const current = selectionRef.current;
      if (current.projectId !== undefined) return false;
      if (current.chatInstanceId !== forChatInstanceId) return false;
      setSelection((previous) =>
        previous.projectId === undefined && previous.chatInstanceId === forChatInstanceId
          ? { ...previous, projectId }
          : previous
      );
      return true;
    },
    [selectionRef]
  );

  const rotateChatLifetime = useCallback(() => {
    setSelection((previous) => ({
      ...previous,
      chatInstanceId: mintChatInstanceId(),
    }));
  }, []);

  const beginProvisionalChat = useCallback(() => {
    const chatInstanceId = mintChatInstanceId();
    setSelection({ projectId: undefined, chatInstanceId });
    return chatInstanceId;
  }, []);

  const subscribeRef = useLatestRef(subscribeToRouteSettled);

  // Second authority: after a bind whose URL update failed, "New chat" pushes the
  // URL we are already on and `routeProjectId` never moves.
  useEffect(
    () =>
      subscribeRef.current((target) => {
        // Leaving the route: rotating the chat this page still shows only churns.
        if (target === null) return;
        setSelection((previous) =>
          previous.projectId === target
            ? previous
            : { projectId: target, chatInstanceId: mintChatInstanceId() }
        );
      }),
    [subscribeRef]
  );

  return {
    ...selection,
    bindProvisionalChat,
    beginProvisionalChat,
    rotateChatLifetime,
  };
};
